import { Component, inject, Input, OnInit } from '@angular/core';
import { AbstractValueAccessorComponent, ValueAccessorUtil } from '@railmybox/shared/util';
import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, switchMap } from 'rxjs/operators';
import { CustomerAdminInfoModel, CustomerModel, CustomerService } from '@railmybox/api-user';

@UntilDestroy()
@Component({
  selector: 'rmb-customer-select',
  templateUrl: './customer-select.component.html',
  styleUrls: ['./customer-select.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(CustomerSelectComponent)],
  standalone: false,
})
export class CustomerSelectComponent extends AbstractValueAccessorComponent<CustomerModel> implements OnInit {
  private customerService = inject(CustomerService);

  customers$: Observable<CustomerModel[]>;
  searchControl: FormControl = new FormControl(undefined);
  selectedName: string;
  @Input() filterCustomers: string;
  @Input() companyOwner: boolean = undefined;
  @Input() active: boolean = undefined;
  @Input() clearable = true;
  @Input() customerList: CustomerModel[] = [];

  ngOnInit(): void {
    const selectedCustomers = this.customerList.map((x) => x.id);
    this.customers$ = this.searchControl.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((query: string) =>
        query
          ? this.customerService.listCustomers({
              isCompanyOwner: this.companyOwner,
              rmbAuthorized: this.active,
              active: this.active,
              query,
            })
          : of([])
      ),
      map((x) => x.filter((item) => item.active === true)),
      map((customers: CustomerAdminInfoModel[]) =>
        this.filterCustomers
          ? customers.filter((customer) =>
              `${customer.id} ${customer.companyName}`.toLowerCase().includes(this.filterCustomers.toLowerCase())
            )
          : customers
      ),
      map((customer: CustomerAdminInfoModel[]) => customer.filter((search) => !selectedCustomers.includes(search.id)))
    );
  }

  onSearchValueChanged(query: string): void {
    this.searchControl.patchValue(query);
  }

  onSelect(model: CustomerAdminInfoModel): void {
    this.updateSelectedName(model);
    this.value = model;
    this.onChange(model);
    this.changed.emit(model);
  }

  private updateSelectedName(model: CustomerAdminInfoModel) {
    const companyData = model ? `${model?.companyName}, ${model?.companyId}` : undefined;
    this.selectedName = model?.lastName ? `${model?.firstName} ${model?.lastName} (${companyData})` : companyData;
  }

  async writeValue(value: CustomerModel): Promise<void> {
    super.writeValue(value);
    if (value) {
      const customer = await this.customerService.showCustomer(typeof value === 'string' ? value : value?.id).toPromise();

      // In case no entry found with that id
      if (!customer) {
        return;
      }

      this.value = customer;
      this.updateSelectedName(customer);
    } else {
      this.value = undefined;
      this.selectedName = undefined;
    }
  }
}
